/**
 * Beheert abonnementen en persistentie voor projecten en contexten (tags) in Firestore.
 */
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { resolveTermDisplayName } from '@/services/tagNameUtils';
import {
  pickLeastUsedColorIndex,
  colorIndicesFromMap,
} from '@/utils/projectColorPalette';

const iterSnapshotDocs = (snapshot) => {
  if (!snapshot) return [];
  if (typeof snapshot.forEach === 'function') {
    const docs = [];
    snapshot.forEach((docSnap) => docs.push(docSnap));
    return docs;
  }
  return snapshot.docs ?? [];
};

const collectProjectColorIndices = (snapshot) => {
  const indices = [];
  for (const projectDoc of iterSnapshotDocs(snapshot)) {
    const data = projectDoc.data();
    if (data && Number.isInteger(data.color)) {
      indices.push(data.color);
    }
  }
  return indices;
};

/**
 * @param {string} term
 * @param {'projects'|'contexts'} collectionName
 * @param {string} userId
 * @param {Map<string, string> | Record<string, string> | null} nameById
 * @param {number[] | null} projectColorIndices
 * @returns {Promise<number[] | null>}
 */
const ensureSingleTerm = async (
  term,
  collectionName,
  userId,
  nameById,
  projectColorIndices,
) => {
  const termId = term.toLowerCase();
  const termRef = doc(db, `users/${userId}/${collectionName}`, termId);
  const termSnap = await getDoc(termRef);
  if (termSnap.exists()) {
    return projectColorIndices;
  }
  const name = resolveTermDisplayName(term, nameById);
  const payload = { name };
  let nextIndices = projectColorIndices;
  if (collectionName === 'projects') {
    const color = pickLeastUsedColorIndex(projectColorIndices);
    payload.color = color;
    nextIndices = [...(projectColorIndices ?? []), color];
  }
  await setDoc(termRef, payload);
  return nextIndices;
};

/**
 * @param {string[]} terms — lowercase tag ids
 * @param {'projects'|'contexts'} collectionName
 * @param {string} userId
 * @param {Map<string, string> | Record<string, string> | null} [nameById]
 */
export const ensureTermsExist = async (
  terms,
  collectionName,
  userId,
  nameById = null,
) => {
  if (!userId || !terms || terms.length === 0) return;
  try {
    let projectColorIndices = null;
    if (collectionName === 'projects') {
      const projectsRef = collection(db, `users/${userId}/projects`);
      const projectsSnap = await getDocs(projectsRef);
      projectColorIndices = collectProjectColorIndices(projectsSnap);
    }

    for (const term of terms) {
      if (!term) continue;
      projectColorIndices = await ensureSingleTerm(
        term,
        collectionName,
        userId,
        nameById,
        projectColorIndices,
      );
    }
  } catch (error) {
    console.error(
      `TagService: Error ensuring terms exist in ${collectionName}:`,
      error,
    );
    throw error;
  }
};

/**
 * @param {string} userId
 * @param {string[]} missingColorIds — sorted externally optional; will sort here
 * @param {Map<string, number>} existingColorMap
 */
export const backfillMissingProjectColors = async (
  userId,
  missingColorIds,
  existingColorMap,
) => {
  if (!userId || !missingColorIds?.length) return;

  const sortedIds = [...missingColorIds].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  let indices = colorIndicesFromMap(existingColorMap);
  const batch = writeBatch(db);

  for (const termId of sortedIds) {
    const color = pickLeastUsedColorIndex(indices);
    indices = [...indices, color];
    const termRef = doc(db, `users/${userId}/projects`, termId);
    batch.set(termRef, { color }, { merge: true });
  }

  await batch.commit();
};

export const subscribeToProjects = (userId, onNext, onError) => {
  const projectsCollectionRef = collection(db, 'users', userId, 'projects');
  return onSnapshot(projectsCollectionRef, onNext, (error) => {
    console.error('TagService: Error listening to projects collection:', error);
    if (onError) onError(error);
  });
};

export const subscribeToContexts = (userId, onNext, onError) => {
  const contextsCollectionRef = collection(db, 'users', userId, 'contexts');
  return onSnapshot(contextsCollectionRef, onNext, (error) => {
    console.error('TagService: Error listening to contexts collection:', error);
    if (onError) onError(error);
  });
};
